import { useState } from "react";
import { FetchResponse, Image, PflegeCategory } from "../../shared/types";

export default function useFetch(baseUrl: string) {
  const getCSRFTokenFromCookie = (): string | undefined => {
    return document.cookie
      .split("; ")
      .find((row) => row.startsWith("XSRF-TOKEN="))
      ?.split("=")[1];
  };

  const [loading, setLoading] = useState(true);

  function deleteImage(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      fetch(baseUrl + url, {
        method: "delete",
        headers: {
          "X-XSRF-TOKEN": `${getCSRFTokenFromCookie()}`,
        },
      })
        .then((response) => {
          if (!response.ok) {
            return reject(response);
          }
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  function deleteTattooImage(id: number): Promise<void> {
    return deleteImage(`api/admin/tattoo-images/${id}`);
  }

  function deleteWannaDoImage(id: number): Promise<void> {
    return deleteImage(`api/admin/wanna-do-images/${id}`);
  }

  function deletePiercingImage(id: number): Promise<void> {
    return deleteImage(`api/admin/piercing-images/${id}`);
  }

  function getTattooImages(): Promise<{ images: Image[] }> {
    return new Promise((resolve, reject) => {
      fetch(baseUrl + "api/tattoo-images")
        .then((response) => response.json())
        .then((data) => {
          if (!data) {
            setLoading(false);
            return reject(data);
          }
          setLoading(false);
          resolve(data);
        })
        .catch((error) => {
          setLoading(false);
          reject(error);
        });
    });
  }

  function getWannaDoImages(): Promise<{ images: Image[] }> {
    return new Promise((resolve, reject) => {
      fetch(baseUrl + "api/wanna-do-images")
        .then((response) => response.json())
        .then((data) => {
          if (!data) {
            setLoading(false);
            return reject(data);
          }
          setLoading(false);
          resolve(data);
        })
        .catch((error) => {
          setLoading(false);
          reject(error);
        });
    });
  }

  function getPiercingImages(): Promise<{ images: Image[] }> {
    return new Promise((resolve, reject) => {
      fetch(baseUrl + "api/piercing-images")
        .then((response) => response.json())
        .then((data) => {
          if (!data) {
            setLoading(false);
            return reject(data);
          }
          setLoading(false);
          resolve(data);
        })
        .catch((error) => {
          setLoading(false);
          reject(error);
        });
    });
  }

  function getPflegeCategories(): Promise<PflegeCategory[]> {
    return new Promise((resolve, reject) => {
      fetch(baseUrl + "api/pflege-categories")
        .then((response) => response.json())
        .then((data) => {
          if (!data) {
            setLoading(false);
            return reject(data);
          }
          setLoading(false);
          resolve(data);
        })
        .catch((error) => {
          setLoading(false);
          reject(error);
        });
    });
  }

  // Unauthenticated requests get 302-redirected to the /login HTML page,
  // which would otherwise blow up json() with "Unexpected token '<'".
  function assertJsonResponse(response: Response): Promise<any> {
    if (response.redirected) {
      throw new Error(
        `Request was redirected to ${response.url} — session expired or not logged in`,
      );
    }
    if (!response.ok) {
      throw new Error(`Request failed with HTTP ${response.status}`);
    }
    return response.json();
  }

  function post(url: string, body: any) {
    return new Promise((resolve, reject) => {
      fetch(baseUrl + url, {
        method: "post",
        headers: {
          "Content-Type": "application/json",
          "X-XSRF-TOKEN": `${getCSRFTokenFromCookie()}`,
        },
        body: JSON.stringify(body),
      })
        .then(assertJsonResponse)
        .then((data) => {
          if (!data) {
            setLoading(false);
            return reject(data);
          }
          setLoading(false);
          resolve(data);
        })
        .catch((error) => {
          setLoading(false);
          reject(error);
        });
    });
  }

  function postForm(url: string, body: FormData): Promise<FetchResponse> {
    return new Promise((resolve, reject) => {
      fetch(baseUrl + url, {
        method: "post",
        headers: {
          "X-XSRF-TOKEN": `${getCSRFTokenFromCookie()}`,
        },
        body,
      })
        .then(assertJsonResponse)
        .then((data) => {
          if (!data) {
            setLoading(false);
            return reject(data);
          }
          setLoading(false);
          resolve(data);
        })
        .catch((error) => {
          setLoading(false);
          reject(error);
        });
    });
  }

  // Unlike postForm, this rejects with the raw Response on failure so
  // callers can read the backend's German validation message from the body.
  function submitPflegeForm(
    url: string,
    body: FormData,
  ): Promise<FetchResponse> {
    return new Promise((resolve, reject) => {
      fetch(baseUrl + url, {
        method: "post",
        headers: {
          "X-XSRF-TOKEN": `${getCSRFTokenFromCookie()}`,
        },
        body,
      })
        .then((response) => {
          if (response.redirected || !response.ok) {
            setLoading(false);
            return reject(response);
          }
          return response.json().then((data) => {
            setLoading(false);
            resolve(data);
          });
        })
        .catch((error) => {
          setLoading(false);
          reject(error);
        });
    });
  }

  function createPflegeCategory(body: FormData): Promise<FetchResponse> {
    return submitPflegeForm("api/admin/pflege-categories", body);
  }

  function updatePflegeCategory(
    id: number,
    body: FormData,
  ): Promise<FetchResponse> {
    return submitPflegeForm(`api/admin/pflege-categories/${id}`, body);
  }

  function deletePflegeCategory(id: number): Promise<void> {
    return deleteImage(`api/admin/pflege-categories/${id}`);
  }

  return {
    getTattooImages,
    getWannaDoImages,
    getPiercingImages,
    getPflegeCategories,
    createPflegeCategory,
    updatePflegeCategory,
    deletePflegeCategory,
    deleteTattooImage,
    deleteWannaDoImage,
    deletePiercingImage,
    post,
    postForm,
    loading,
  };
}
